import { useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import type { Language } from '@darkchat/types'
import { calculateCorruptionLevel } from '../utils/corruptionLevel'
import { applyCorruption, getCorruptionConfig } from '../utils/textCorruption'
import { calculateTypewriterSpeed } from '../utils/typewriterSpeed'
import { TypewriterText } from '../components/chat/TypewriterText'

const DEFAULT_TEXT_JA = 'これは深淵の底から響く声。痛みと苦悶が文字を蝕んでいく。'
const DEFAULT_TEXT_EN = 'A voice echoes from the depths of the abyss. Pain and anguish corrode each letter, consuming all meaning.'

const PRESETS = [
  { nameJa: '正常', nameEn: 'Normal', pain: 0, suffering: 0 },
  { nameJa: '微弱', nameEn: 'Faint', pain: 15, suffering: 10 },
  { nameJa: '軽度', nameEn: 'Mild', pain: 35, suffering: 20 },
  { nameJa: '中度', nameEn: 'Moderate', pain: 55, suffering: 40 },
  { nameJa: '重度', nameEn: 'Severe', pain: 75, suffering: 60 },
  { nameJa: '深刻', nameEn: 'Critical', pain: 90, suffering: 80 },
  { nameJa: '極限', nameEn: 'Extreme', pain: 100, suffering: 100 }
]

export function CorruptionTest() {
  const location = useLocation()
  // URLから言語を判定 (/ja/... または /en/...)
  const language: Language = location.pathname.startsWith('/ja') ? 'ja' : 'en'

  const [pain, setPain] = useState(50)
  const [suffering, setSuffering] = useState(30)
  const [text, setText] = useState(language === 'ja' ? DEFAULT_TEXT_JA : DEFAULT_TEXT_EN)
  const [resetKey, setResetKey] = useState(0)

  const corruptionLevel = calculateCorruptionLevel(pain, suffering)
  const corruptedText = applyCorruption(text, corruptionLevel)
  const config = getCorruptionConfig(corruptionLevel, text)
  const typewriterSpeed = calculateTypewriterSpeed(language, suffering)

  const handlePreset = (preset: typeof PRESETS[0]) => {
    setPain(preset.pain)
    setSuffering(preset.suffering)
  }

  const handleReplay = () => {
    setResetKey(prev => prev + 1)
  }

  // グローバルのoverflow: hiddenを上書きしてスクロールを有効化
  useEffect(() => {
    document.documentElement.style.overflow = 'auto'
    document.body.style.overflow = 'auto'

    return () => {
      document.documentElement.style.overflow = 'hidden'
      document.body.style.overflow = 'hidden'
    }
  }, [])

  return (
    <div className="min-h-screen bg-black text-gray-300 p-8 overflow-y-auto">
      <div className="max-w-6xl mx-auto space-y-8">
        {/* Header */}
        <div className="text-center space-y-2">
          <h1 className="text-3xl font-bold text-red-500">
            {language === 'ja' ? 'テキスト腐敗テスト' : 'Text Corruption Test'}
          </h1>
          <p className="text-gray-500">
            {language === 'ja'
              ? '痛みと苦悶のパラメータを調整して、テキスト腐敗効果を確認できます'
              : 'Adjust pain and suffering parameters to preview text corruption effects'}
          </p>
        </div>

        {/* Controls */}
        <div className="bg-gray-900 border border-gray-800 p-6 space-y-6">
          <h2 className="text-xl font-bold text-red-400">
            {language === 'ja' ? 'パラメータ' : 'Parameters'}
          </h2>

          {/* Pain Slider */}
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <label className="text-sm font-medium text-gray-400">
                {language === 'ja' ? '痛み (Pain)' : 'Pain'}
              </label>
              <span className="text-lg font-mono text-red-400">{pain}</span>
            </div>
            <input
              type="range"
              min="0"
              max="100"
              value={pain}
              onChange={(e) => setPain(Number(e.target.value))}
              className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-red-500"
            />
          </div>

          {/* Suffering Slider */}
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <label className="text-sm font-medium text-gray-400">
                {language === 'ja' ? '苦悶 (Suffering)' : 'Suffering'}
              </label>
              <span className="text-lg font-mono text-purple-400">{suffering}</span>
            </div>
            <input
              type="range"
              min="0"
              max="100"
              value={suffering}
              onChange={(e) => setSuffering(Number(e.target.value))}
              className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-purple-500"
            />
          </div>

          {/* Corruption Level Display */}
          <div className="bg-gray-800 p-4 rounded border border-gray-700">
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-gray-400">
                {language === 'ja' ? '腐敗レベル' : 'Corruption Level'}
              </span>
              <span className="text-2xl font-bold text-yellow-400">{corruptionLevel.toFixed(2)}</span>
            </div>
            <div className="mt-2 text-xs text-gray-500">
              {language === 'ja'
                ? `計算式: pain × (1 + suffering / 150) = ${pain} × (1 + ${suffering} / 150)`
                : `Formula: pain × (1 + suffering / 150) = ${pain} × (1 + ${suffering} / 150)`}
            </div>
          </div>

          {/* Presets */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-400">
              {language === 'ja' ? 'プリセット' : 'Presets'}
            </label>
            <div className="grid grid-cols-4 gap-2">
              {PRESETS.map((preset, index) => (
                <button
                  key={index}
                  onClick={() => handlePreset(preset)}
                  className="px-3 py-2 bg-gray-800 hover:bg-gray-700 border border-gray-700 hover:border-gray-600 text-sm transition-colors"
                >
                  {language === 'ja' ? preset.nameJa : preset.nameEn}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Text Input */}
        <div className="bg-gray-900 border border-gray-800 p-6 space-y-4">
          <h2 className="text-xl font-bold text-red-400">
            {language === 'ja' ? 'サンプルテキスト' : 'Sample Text'}
          </h2>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={3}
            className="w-full bg-gray-800 border border-gray-700 p-3 text-gray-300 focus:outline-none focus:border-gray-600"
            placeholder={language === 'ja' ? 'テキストを入力...' : 'Enter text...'}
          />
        </div>

        {/* Preview */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Original */}
          <div className="bg-gray-900 border border-gray-800 p-6 space-y-4">
            <h3 className="text-lg font-bold text-green-400">
              {language === 'ja' ? '元のテキスト' : 'Original Text'}
            </h3>
            <div className="bg-gray-800 border border-gray-700 p-4 min-h-[200px]">
              <p className="text-gray-300 whitespace-pre-wrap break-words">{text}</p>
            </div>
            <div className="text-xs text-gray-500">
              {language === 'ja' ? `文字数: ${text.length}` : `Characters: ${text.length}`}
            </div>
          </div>

          {/* Corrupted */}
          <div className="bg-gray-900 border border-gray-800 p-6 space-y-4">
            <h3 className="text-lg font-bold text-red-400">
              {language === 'ja' ? '腐敗後テキスト（静的）' : 'Corrupted Text (Static)'}
            </h3>
            <div className="bg-gray-800 border border-gray-700 p-4 min-h-[200px]">
              <p className="text-red-300 whitespace-pre-wrap break-words">{corruptedText}</p>
            </div>
            <div className="text-xs text-gray-500">
              {language === 'ja' ? `文字数: ${corruptedText.length}` : `Characters: ${corruptedText.length}`}
            </div>
          </div>

          {/* Typewriter Preview */}
          <div className="bg-gray-900 border border-gray-800 p-6 space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-bold text-purple-400">
                {language === 'ja' ? 'タイプライター表示' : 'Typewriter Display'}
              </h3>
              <button
                onClick={handleReplay}
                className="px-3 py-1 bg-purple-800 hover:bg-purple-700 border border-purple-600 text-xs transition-colors"
              >
                {language === 'ja' ? '再生' : 'Replay'}
              </button>
            </div>
            <div className="bg-gray-800 border border-gray-700 p-4 min-h-[200px]">
              <TypewriterText
                text={corruptedText}
                speed={typewriterSpeed}
                resetTrigger={resetKey}
                className="text-purple-300 whitespace-pre-wrap break-words"
              />
            </div>
            <div className="text-xs text-gray-500">
              {language === 'ja'
                ? `速度: ${typewriterSpeed}ms/文字`
                : `Speed: ${typewriterSpeed}ms/char`}
            </div>
          </div>
        </div>

        {/* Config Details */}
        <div className="bg-gray-900 border border-gray-800 p-6 space-y-4">
          <h2 className="text-xl font-bold text-red-400">
            {language === 'ja' ? '適用される設定' : 'Applied Configuration'}
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Insert Rate */}
            <div className="bg-gray-800 p-4 rounded border border-gray-700">
              <div className="text-sm text-gray-400 mb-1">
                {language === 'ja' ? '挿入率' : 'Insert Rate'}
              </div>
              <div className="text-2xl font-bold text-yellow-400">
                {(config.insertRate * 100).toFixed(0)}%
              </div>
            </div>

            {/* Corruption Stage */}
            <div className="bg-gray-800 p-4 rounded border border-gray-700">
              <div className="text-sm text-gray-400 mb-1">
                {language === 'ja' ? '腐敗段階' : 'Corruption Stage'}
              </div>
              <div className="text-2xl font-bold text-purple-400">
                {corruptionLevel < 20 ? '1/6' :
                 corruptionLevel < 40 ? '2/6' :
                 corruptionLevel < 60 ? '3/6' :
                 corruptionLevel < 80 ? '4/6' :
                 corruptionLevel < 95 ? '5/6' : '6/6'}
              </div>
            </div>

            {/* Character Set Size */}
            <div className="bg-gray-800 p-4 rounded border border-gray-700">
              <div className="text-sm text-gray-400 mb-1">
                {language === 'ja' ? '使用文字種' : 'Character Set'}
              </div>
              <div className="text-2xl font-bold text-blue-400">
                {config.chars.length}
              </div>
            </div>

            {/* Typewriter Speed */}
            <div className="bg-gray-800 p-4 rounded border border-gray-700">
              <div className="text-sm text-gray-400 mb-1">
                {language === 'ja' ? 'タイプ速度' : 'Type Speed'}
              </div>
              <div className="text-2xl font-bold text-cyan-400">
                {typewriterSpeed}ms
              </div>
            </div>
          </div>

          {/* Characters */}
          <div className="bg-gray-800 p-4 rounded border border-gray-700">
            <div className="text-sm text-gray-400 mb-2">
              {language === 'ja' ? '挿入される文字' : 'Inserted Characters'}
            </div>
            <div className="flex flex-wrap gap-2">
              {config.chars.length > 0 ? (
                config.chars.map((char, i) => (
                  <span key={i} className="px-3 py-1 bg-gray-700 border border-gray-600 font-mono text-sm">
                    {char === ' ' ? '␣' : (char as string) === '　' ? '□' : char}
                  </span>
                ))
              ) : (
                <span className="text-gray-500 text-sm">
                  {language === 'ja' ? '(なし)' : '(None)'}
                </span>
              )}
            </div>
          </div>

          {/* Stage Info */}
          <div className="bg-gray-800 p-4 rounded border border-gray-700">
            <div className="text-sm text-gray-400 mb-2">
              {language === 'ja' ? '段階情報' : 'Stage Information'}
            </div>
            <div className="space-y-1 text-sm font-mono">
              <div className={corruptionLevel < 20 ? 'text-yellow-400' : 'text-gray-600'}>
                0-20: {language === 'ja' ? '挿入率15%' : 'Insert 15%'}
              </div>
              <div className={corruptionLevel >= 20 && corruptionLevel < 40 ? 'text-yellow-400' : 'text-gray-600'}>
                20-40: {language === 'ja' ? '挿入率20%' : 'Insert 20%'}
              </div>
              <div className={corruptionLevel >= 40 && corruptionLevel < 60 ? 'text-yellow-400' : 'text-gray-600'}>
                40-60: {language === 'ja' ? '挿入率25%' : 'Insert 25%'}
              </div>
              <div className={corruptionLevel >= 60 && corruptionLevel < 80 ? 'text-yellow-400' : 'text-gray-600'}>
                60-80: {language === 'ja' ? '挿入率35%' : 'Insert 35%'}
              </div>
              <div className={corruptionLevel >= 80 && corruptionLevel < 95 ? 'text-yellow-400' : 'text-gray-600'}>
                80-95: {language === 'ja' ? '挿入率45%' : 'Insert 45%'}
              </div>
              <div className={corruptionLevel >= 95 ? 'text-yellow-400' : 'text-gray-600'}>
                95-100: {language === 'ja' ? '挿入率55%' : 'Insert 55%'}
              </div>
            </div>
          </div>
        </div>

        {/* Back Button */}
        <div className="flex justify-center">
          <a
            href={`/${language}`}
            className="px-8 py-3 bg-gray-800 hover:bg-gray-700 border border-gray-700 hover:border-gray-600 transition-colors"
          >
            {language === 'ja' ? 'ホームに戻る' : 'Back to Home'}
          </a>
        </div>
      </div>
    </div>
  )
}
